import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  MagnifyingGlassIcon, 
  XMarkIcon
} from '@heroicons/react/24/outline';
import { 
  usePipelines 
} from '../../services/queries';
import { 
  useAssociateNodePipelineLink,
  useUpdateNodePipelineLink
} from '../../hooks/useReleaseFlow';
import { LoadingSpinner, EmptyState } from '../common';
import type { 
  PipelineDto
} from '../../types/pipelineFlow';

interface PipelineSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  releaseFlowId: string;
  nodeId: string;
  nodeName: string;
  existingPipeline?: { pipelineDefinitionId: number; pipelineName: string };
  onPipelineChange?: () => void;
}

export const PipelineSelectionModal: React.FC<PipelineSelectionModalProps> = ({
  isOpen,
  onClose,
  releaseFlowId,
  nodeId,
  nodeName,
  existingPipeline,
  onPipelineChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState<PipelineDto | null>(null);

  // Fetch pipelines from local DB
  const { data: pipelines, isLoading: pipelinesLoading } = usePipelines();
  
  // Mutation hooks for pipeline operations
  const associateNodePipelineLink = useAssociateNodePipelineLink();
  const updateNodePipelineLink = useUpdateNodePipelineLink();

  // Effect to set initial selected pipeline based on existing association
  useEffect(() => {
    if (pipelines && existingPipeline && isOpen) {
      const existingPipelineObj = pipelines.find(
        (p: PipelineDto) => p.definitionId === existingPipeline.pipelineDefinitionId
      );
      if (existingPipelineObj) {
        setSelectedPipeline(existingPipelineObj);
        
        // Scroll to the existing pipeline in the list after a short delay
        setTimeout(() => {
          const element = document.getElementById(`pipeline-${existingPipelineObj.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } else if (!existingPipeline && isOpen) {
      // Reset selection if no existing pipeline
      setSelectedPipeline(null);
    }
  }, [pipelines, existingPipeline, isOpen]);

  // Filter pipelines based on search term
  const filteredPipelines = pipelines?.filter((pipeline: PipelineDto) =>
    pipeline.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pipeline.pipelineName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handlePipelineSelect = (pipeline: PipelineDto) => {
    setSelectedPipeline(pipeline);
  };

  const handleAssociatePipeline = async () => {
    if (!selectedPipeline) return;
    
    try {
      if (existingPipeline) {
        // Update existing pipeline association
        await updateNodePipelineLink.mutateAsync({
          releaseFlowId,
          nodeId,
          data: {
            pipelineId: selectedPipeline.id,
            pipelineDefinitionId: selectedPipeline.definitionId
          }
        });
      } else {
        // Create new pipeline association
        await associateNodePipelineLink.mutateAsync({
          releaseFlowId,
          nodeId,
          data: {
            releaseFlowNodeId: nodeId,
            pipelineId: selectedPipeline.id,
            pipelineDefinitionId: selectedPipeline.definitionId
          }
        });
      }
      
      onPipelineChange?.();
      onClose();
    } catch (error) {
      console.error('Failed to associate pipeline:', error);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Pipeline Selection
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Select pipelines for node: <span className="font-medium">{nodeName}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className={`flex h-96 flex-col`}>
                  {/* Pipelines Panel */}
                  <div className="flex-1">
                    {/* Search Bar */}
                    <div className="p-4 border-b border-gray-200">
                      <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 ml-3" />
                        <input
                          type="text"
                          className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Search pipelines..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Pipelines List */}
                    <div className="p-4 overflow-y-auto h-80">
                      {pipelinesLoading ? (
                        <div className="flex justify-center py-8">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : filteredPipelines.length ? (
                        <div className="space-y-3">
                          {filteredPipelines.map((pipeline: PipelineDto) => {
                            const isSelected = selectedPipeline?.id === pipeline.id;
                            return (
                              <div
                                key={pipeline.id}
                                className={`relative flex items-start p-4 rounded-lg border transition-all cursor-pointer ${
                                  isSelected 
                                    ? 'border-indigo-200 bg-indigo-50' 
                                    : 'border-gray-200 hover:border-gray-300 bg-white'
                                }`}
                                onClick={() => handlePipelineSelect(pipeline)}
                              >
                                <div className="flex-shrink-0 mr-3">
                                  <input
                                    id={`pipeline-${pipeline.id}`}
                                    name="selectedPipeline"
                                    type="radio"
                                    checked={isSelected}
                                    onChange={() => handlePipelineSelect(pipeline)}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <label 
                                    htmlFor={`pipeline-${pipeline.id}`}
                                    className={`block text-sm font-medium cursor-pointer ${pipeline.isDeleted ? 'text-amber-700 line-through' : 'text-gray-900'}`}
                                  >
                                    {pipeline.name}
                                    {pipeline.isDeleted && (
                                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 no-underline">
                                        Removed
                                      </span>
                                    )}
                                  </label>
                                  <p className="text-sm text-gray-500 mt-1">
                                    {pipeline.pipelineName} • Definition: {pipeline.definitionId}
                                  </p>
                                  <div className="flex items-center mt-2 space-x-4">
                                    <span className="text-xs text-gray-400">
                                      {pipeline.deploymentGroupName || 'No group'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <EmptyState 
                          title="No pipelines found"
                          description="Try adjusting your search terms or check if any pipelines exist."
                          icon={<MagnifyingGlassIcon className="h-12 w-12" />}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                  <div className="text-sm text-gray-500">
                    {selectedPipeline ? `Selected: ${selectedPipeline.name}` : 'No pipeline selected'}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    {selectedPipeline && (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleAssociatePipeline}
                        disabled={associateNodePipelineLink.isPending}
                      >
                        {associateNodePipelineLink.isPending ? 'Associating...' : 'Associate Pipeline'}
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PipelineSelectionModal;